import React from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  Paper,
} from '@mui/material';
import {
  People,
  EmojiEvents,
  LocalShipping,
  Home,
} from '@mui/icons-material';

const AboutPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          About New Life Foodbarn
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Serving the Gold Coast community with love, support and hope since 2008
        </Typography>
        <Typography variant="body1" sx={{ maxWidth: 600, mx: 'auto', mb: 4 }}>
          New Life Community Care Foodbarn was born in 2008 to reach out into the Gold Coast community with love, 
          support and hope. Since the opening, many volunteers have enabled us to reach out and help thousands of people in need.
        </Typography>
      </Box>

      {/* Mission & Vision */}
      <Box sx={{ mb: 6 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <EmojiEvents sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h4" component="h2">
                    Our Mission
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  To provide affordable fresh food from local markets and handpicked goods from supermarkets 
                  and other companies to people experiencing financial difficulties, pensioners, health care 
                  and senior card holders.
                </Typography>
                <Typography variant="body1" paragraph>
                  We believe that loving support and acceptance is a vital key in helping so many who have 
                  suffered loss, hardship, neglect, abuse or health issues that have come their way.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <People sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h4" component="h2">
                    Our Vision
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  To be a leading community care organization that provides comprehensive support services 
                  to the Gold Coast community, helping thousands of people in need.
                </Typography>
                <Typography variant="body1" paragraph>
                  We hope to, in the future, on our 3-acre complex, have another 30 units for over 50's 
                  with affordable rents and opportunities to work in our welfare services, helping others 
                  and giving back to our community.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Our Story */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Our Story
        </Typography>
        <Card sx={{ p: 4 }}>
          <CardContent>
            <Typography variant="body1" paragraph>
              New Life Community Care Foodbarn was born in 2008 to reach out into the Gold Coast community with love, 
              support and hope. Since the opening, many volunteers have enabled us to reach out and help thousands of people in need.
            </Typography>
            <Typography variant="body1" paragraph>
              We believe that loving support and acceptance is a vital key in helping so many who have suffered loss, 
              hardship, neglect, abuse or health issues that have come their way. In order to further address this we also 
              run a well accepted 12 step recovery program to help make a difference in our Gold Coast community.
            </Typography>
            <Typography variant="body1" paragraph>
              New Life Community Care Accommodation exists for those who may require assistance with emergency accommodation.
            </Typography>
            <Typography variant="body1" paragraph>
              The amazing work achieved in our community would not be possible without the assistance of some 15-25 
              committed volunteers each day.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Timeline */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Our Journey
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" component="span" color="primary">
                2008
              </Typography>
              <Typography>New Life Community Care Foodbarn was established to serve the Gold Coast community</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" component="span" color="primary">
                Growth
              </Typography>
              <Typography>Expanded to support over 17 charities and feed thousands of mouths each month</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" component="span" color="primary">
                Community Impact
              </Typography>
              <Typography>Became a registered workplace for Community Services, Centrelink & SPER</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" component="span" color="primary">
                Future Vision
              </Typography>
              <Typography>Planning 30 units for over 50's with affordable rents and welfare service opportunities</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Impact Statistics */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Our Impact
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" color="primary.main" gutterBottom>
                17+
              </Typography>
              <Typography variant="h6" gutterBottom>
                Charities Supported
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We support over 17 charities in our community
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" color="primary.main" gutterBottom>
                1000s
              </Typography>
              <Typography variant="h6" gutterBottom>
                People Fed Monthly
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We feed thousands of mouths each month
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" color="primary.main" gutterBottom>
                25-30k
              </Typography>
              <Typography variant="h6" gutterBottom>
                Tons Saved
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tons of waste food saved from landfill monthly
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ textAlign: 'center', p: 3 }}>
              <Typography variant="h3" color="primary.main" gutterBottom>
                15-25
              </Typography>
              <Typography variant="h6" gutterBottom>
                Daily Volunteers
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Committed volunteers working each day
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Services */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Our Services
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <LocalShipping sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Food Distribution
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  We provide cheaper than normal rates for fresh food from local markets and handpicked goods 
                  from supermarkets and other companies.
                </Typography>
                <Typography variant="body1" paragraph>
                  Our food parcels are available for pickup and delivery on Wednesday & Thursday each week.
                </Typography>
                <Typography variant="body1">
                  We welcome people experiencing financial difficulties, pensioners, health care and senior card holders.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Home sx={{ fontSize: 40, color: 'primary.main' }} />
                  <Typography variant="h5" component="h3">
                    Accommodation Support
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                  New Life Community Care Accommodation exists for those who may require assistance with emergency accommodation.
                </Typography>
                <Typography variant="body1" paragraph>
                  We provide support and assistance to those in need of temporary housing solutions.
                </Typography>
                <Typography variant="body1">
                  Our accommodation services are part of our comprehensive community care approach.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Recovery Program */}
      <Box sx={{ mb: 6 }}>
        <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h2" gutterBottom>
              12-Step Recovery Program
            </Typography>
            <Typography variant="h6" paragraph>
              Making a difference in our Gold Coast community
            </Typography>
            <Typography variant="body1" paragraph>
              In order to further address the needs of those who have suffered loss, hardship, neglect, 
              abuse or health issues, we run a well accepted 12 step recovery program to help make a 
              difference in our Gold Coast community.
            </Typography>
            <Alert severity="info" sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.1)' }}>
              <Typography variant="body2">
                <strong>Support:</strong> Our program provides loving support and acceptance as a vital key 
                in helping people overcome their challenges.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      </Box>

      {/* Organization Details */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
          Organization Details
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Legal Name
              </Typography>
              <Typography variant="body1">
                New Life Community Care Trading as New Life Foodbarn
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                ABN
              </Typography>
              <Typography variant="body1">
                72145513711
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Status
              </Typography>
              <Typography variant="body1">
                D.G.R. Benevolent Institution / Charity
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AboutPage; 